// viewport.ts — device-class responsive helpers (renderer-only). The handoff ships a 5×6 board on
// phones (primary, portrait) and a 6×6 board on tablet (3:4) + desktop (16:10). The Core board
// generator is fully {rows,cols}-parameterized via `config.difficultyForBoard(idx, base, …)`, so the
// 6×6 stage is a RENDERER breakpoint that passes a different `base` grid — NO change to the frozen
// `src/core/**` or `src/data/**`. Phone keeps DEFAULT_GRID, so existing seeds/determinism/saves are
// untouched; 6×6 is a purely additive deterministic path (locked by a 6×6 case in scripts/smoke/sim.ts).
import { DEFAULT_GRID } from '../core';

export type Grid = { rows: number; cols: number };

/** The large-stage board (tablet 3:4 / desktop 16:10). */
export const LARGE_GRID: Grid = { rows: 6, cols: 6 };

/** Real CSS viewport — NOT the Phaser Scale.FIT design space (fixed 720×1280 in main.ts). */
function viewportSize(): { w: number; h: number } {
  if (typeof window === 'undefined') return { w: 360, h: 640 };
  return { w: window.innerWidth || 360, h: window.innerHeight || 640 };
}

/**
 * True for the tablet/desktop device class (the 6×6 stage):
 * - a tablet in any orientation has its SHORT edge ≥ 700 (e.g. 768×1024), and
 * - a desktop is landscape and ≥ 1024 wide.
 * Phones (≤ ~430 short edge) fall through to false.
 */
export function isLargeStage(size: { w: number; h: number } = viewportSize()): boolean {
  const { w, h } = size;
  return Math.min(w, h) >= 700 || (w > h && w >= 1024);
}

/** Board grid for the current device class: 6×6 on tablet/desktop, else the phone default (5×6). */
export function gridForViewport(size: { w: number; h: number } = viewportSize()): Grid {
  return isLargeStage(size) ? { ...LARGE_GRID } : { rows: DEFAULT_GRID.rows, cols: DEFAULT_GRID.cols };
}
